// NEXIS Virtual Lab V0.1 — synthetic, deterministic data engine.
// ALL data here is SIMULATED. No real operator networks are contacted.

export const SIM_DISCLAIMER = {
  pt: "DADOS SIMULADOS — NÃO É UMA MEDIÇÃO REAL DE CAMPO",
  en: "SIMULATED DATA — NOT A REAL FIELD MEASUREMENT",
};

export const CATEGORIES = ["Hotel", "Resort", "Enterprise", "Rural Network"];

export const OPERATORS = ["CST", "Unitel STP"];

export const TECHNOLOGIES = ["3G", "LTE (4G)", "5G"];

export const BANDS = {
  "3G": ["B1 (2100 MHz)", "B8 (900 MHz)"],
  "LTE (4G)": ["B3 (1800 MHz)", "B7 (2600 MHz)", "B20 (800 MHz)"],
  "5G": ["n78 (3500 MHz)", "n28 (700 MHz)"],
};

export const BAND_FREQ = {
  "B1 (2100 MHz)": 2100,
  "B8 (900 MHz)": 900,
  "B3 (1800 MHz)": 1800,
  "B7 (2600 MHz)": 2600,
  "B20 (800 MHz)": 800,
  "n78 (3500 MHz)": 3500,
  "n28 (700 MHz)": 700,
};

export const DEVICE_TYPES = [
  { type: "isp", label: "Internet / ISP", icon: "globe", color: "#0E2340" },
  { type: "gateway", label: "Gateway / Firewall", icon: "shield", color: "#0E2340" },
  { type: "router", label: "Router", icon: "router", color: "#155E75" },
  { type: "switch", label: "Switch", icon: "network", color: "#155E75" },
  { type: "ap", label: "Access Point (AP)", icon: "wifi", color: "#0891B2" },
  { type: "repeater", label: "4G/5G Repeater", icon: "radio", color: "#0891B2" },
  { type: "antenna", label: "External Antenna", icon: "antenna", color: "#0891B2" },
  { type: "ups", label: "UPS / BESS", icon: "battery", color: "#0F766E" },
  { type: "client", label: "Client Device", icon: "laptop", color: "#334155" },
];

// --- deterministic PRNG (mulberry32) ---
function strToSeed(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return h >>> 0;
}

function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function round(n, d = 0) {
  const f = Math.pow(10, d);
  return Math.round(n * f) / f;
}

// Operator-dependent baseline so CST vs Unitel differ consistently.
function operatorBias(operator) {
  if (operator === "CST") return { rsrp: 0, rsrq: 0, sinr: 2, rssi: 0 };
  return { rsrp: -8, rsrq: -2, sinr: -3, rssi: -6 }; // Unitel STP slightly weaker in this sim
}

function techBias(tech) {
  if (tech === "5G") return { rsrp: 6, sinr: 4, bw: 100 };
  if (tech === "LTE (4G)") return { rsrp: 0, sinr: 0, bw: 20 };
  return { rsrp: -6, sinr: -3, bw: 5 }; // 3G
}

// Core KPI generator — fully deterministic from (operator, tech, band).
export function generateKpis(operator, tech, band) {
  const seed = strToSeed(`${operator}|${tech}|${band}`);
  const rand = mulberry32(seed);
  const ob = operatorBias(operator);
  const tb = techBias(tech);

  const rsrp = round(-78 + ob.rsrp + tb.rsrp - rand() * 22);
  const rsrq = round(-9 + ob.rsrq - rand() * 5);
  const rssi = round(-60 + ob.rssi - rand() * 18);
  const sinr = round(8 + ob.sinr + tb.sinr - rand() * 8);
  const pci = Math.floor(rand() * 504);
  const bandwidth = tb.bw;

  return {
    operator,
    technology: tech,
    band,
    freqMhz: BAND_FREQ[band] || 0,
    rsrp,
    rsrq,
    rssi,
    sinr,
    pci,
    bandwidth,
  };
}

// Time series for the chart (08:00 -> 16:00, deterministic jitter).
export function generateSeries(operator, tech, band) {
  const base = generateKpis(operator, tech, band);
  const seed = strToSeed(`${operator}|${tech}|${band}|series`);
  const rand = mulberry32(seed);
  const points = [];
  for (let h = 8; h <= 16; h++) {
    const j = () => (rand() - 0.5) * 6;
    points.push({
      time: `${String(h).padStart(2, "0")}:00`,
      RSRP: round(base.rsrp + j()),
      RSRQ: round(base.rsrq + j() * 0.4),
      SINR: round(base.sinr + j() * 0.6),
    });
  }
  return points;
}

export function signalGrade(sinr) {
  if (sinr >= 13) return { key: "excellent", color: "#16A34A" };
  if (sinr >= 5) return { key: "good", color: "#0891B2" };
  if (sinr >= 0) return { key: "fair", color: "#D97706" };
  return { key: "poor", color: "#DC2626" };
}

// --- Experiment / failure scenarios (deterministic rules) ---
export const SCENARIOS = [
  "primary_isp_outage",
  "backup_activation",
  "ap_failure",
  "increased_latency",
  "packet_loss",
  "power_interruption",
  "ups_operation",
];

// params: { ispDown, backupActive, apDown, latencyUp, packetLoss, powerLoss }
export function runScenario(params, deviceCounts = { ap: 4, client: 20 }) {
  let availability = 100;
  let downtimeMin = 0;
  let latencyMs = 18;
  let packetLossPct = 0;
  let affectedUsers = 0;
  const log = [];

  const push = (level, msg) => log.push({ t: log.length, level, msg });

  push("info", "scenario.start");

  if (params.ispDown) {
    push("critical", "log.isp_down");
    if (params.backupActive) {
      availability -= 2;
      downtimeMin += 2;
      latencyMs += 27;
      affectedUsers += Math.round(deviceCounts.client * 0.1);
      push("warning", "log.backup_engaged");
      push("success", "log.service_restored_backup");
    } else {
      availability -= 100;
      downtimeMin += 45;
      affectedUsers = deviceCounts.client;
      push("critical", "log.total_outage");
    }
  }

  if (params.apDown) {
    const share = deviceCounts.ap > 0 ? 1 / deviceCounts.ap : 0.25;
    availability -= round(share * 20, 1);
    affectedUsers += Math.round(deviceCounts.client * share);
    push("warning", "log.ap_offline");
  }

  if (params.latencyUp) {
    latencyMs += 65;
    availability -= 1;
    push("warning", "log.latency_spike");
  }

  if (params.packetLoss) {
    packetLossPct += 4.5;
    availability -= 3;
    latencyMs += 12;
    push("warning", "log.packet_loss");
  }

  if (params.powerLoss) {
    push("critical", "log.power_lost");
    // UPS always mitigates power loss in this simulation.
    downtimeMin += 0.5;
    availability -= 1;
    push("success", "log.ups_holding");
  }

  availability = Math.max(0, round(availability, 1));
  const status =
    availability >= 97
      ? "stable"
      : availability >= 80
      ? "degraded"
      : availability > 0
      ? "critical"
      : "offline";

  push("info", "scenario.end");

  return {
    availability,
    downtimeMin: round(downtimeMin, 1),
    latencyMs: round(latencyMs),
    packetLossPct: round(packetLossPct, 1),
    affectedUsers,
    status,
    log,
  };
}

// --- Default seed content (one demo project) ---
export function defaultTopology() {
  return {
    nodes: [
      { id: "isp", type: "nexis", position: { x: 300, y: 10 }, data: { kind: "isp", label: "Internet / ISP" } },
      { id: "gw", type: "nexis", position: { x: 300, y: 120 }, data: { kind: "gateway", label: "Gateway / Firewall" } },
      { id: "sw1", type: "nexis", position: { x: 150, y: 240 }, data: { kind: "switch", label: "Switch 1" } },
      { id: "sw2", type: "nexis", position: { x: 460, y: 240 }, data: { kind: "switch", label: "Switch 2" } },
      { id: "ap1", type: "nexis", position: { x: 40, y: 370 }, data: { kind: "ap", label: "AP 01 · Reception" } },
      { id: "ap2", type: "nexis", position: { x: 220, y: 370 }, data: { kind: "ap", label: "AP 02 · Rooms" } },
      { id: "ap3", type: "nexis", position: { x: 400, y: 370 }, data: { kind: "ap", label: "AP 03 · Restaurant" } },
      { id: "ap4", type: "nexis", position: { x: 580, y: 370 }, data: { kind: "ap", label: "AP 04 · Garden" } },
      { id: "ups", type: "nexis", position: { x: 520, y: 120 }, data: { kind: "ups", label: "UPS / BESS" } },
    ],
    edges: [
      { id: "e-isp-gw", source: "isp", target: "gw" },
      { id: "e-gw-sw1", source: "gw", target: "sw1" },
      { id: "e-gw-sw2", source: "gw", target: "sw2" },
      { id: "e-gw-ups", source: "gw", target: "ups" },
      { id: "e-sw1-ap1", source: "sw1", target: "ap1" },
      { id: "e-sw1-ap2", source: "sw1", target: "ap2" },
      { id: "e-sw2-ap3", source: "sw2", target: "ap3" },
      { id: "e-sw2-ap4", source: "sw2", target: "ap4" },
    ],
  };
}

export function seedMeasurements() {
  const combos = [
    ["CST", "LTE (4G)", "B3 (1800 MHz)"],
    ["Unitel STP", "LTE (4G)", "B20 (800 MHz)"],
    ["CST", "5G", "n78 (3500 MHz)"],
  ];
  return combos.map(([op, tech, band], i) => {
    const k = generateKpis(op, tech, band);
    return {
      id: `seed-m-${i}`,
      ...k,
      origin: "simulator",
      timestamp: new Date(Date.now() - (combos.length - i) * 3600_000).toISOString(),
    };
  });
}

export function defaultProject() {
  return {
    id: "proj-hotel-01",
    name: "Hotel Virtual 01 — São Tomé",
    category: "Hotel",
    status: "simulated",
    location: "Fictional site · São Tomé e Príncipe",
    zones: ["Reception", "Rooms", "Restaurant", "Garden / Exterior"],
    engineers: ["eng-admin", "eng-tecnon"],
    notes: "Demonstration project. All data is synthetic.",
    createdAt: new Date().toISOString(),
    topology: defaultTopology(),
    measurements: seedMeasurements(),
    experiments: [],
    signalZones: seedSignalZones(),
    coverageMap: null,
    blueprint: { pages: [] },
    points: [],
  };
}

export function defaultTeam() {
  return [
    { id: "eng-admin", name: "Cleiton Barreto", role: "bsgotech_admin", org: "BSGOTECH", email: "cleiton@bsgotech.com" },
    { id: "eng-tecnon", name: "A. Fernandes", role: "tecnon_engineer", org: "TecNon", email: "a.fernandes@tecnon.example" },
    { id: "eng-rev", name: "External Reviewer", role: "external_reviewer", org: "Partner", email: "reviewer@partner.example" },
  ];
}

export function newId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

// --- Signal Zones (per-zone connectivity survey) ---
export const ZONE_TYPES = ["reception", "rooms", "restaurant", "pool", "garden", "beach", "office", "parking", "exterior", "technical", "other"];
export const ZONE_STATUSES = ["pending", "verified", "archived"];
export const COVERAGE_LEVELS = ["strong", "medium", "weak", "none", "unverified"];
export const WIFI_BANDS = ["2.4 GHz", "5 GHz", "6 GHz"];

// Documented coverage criteria (confirmed by BSGOTECH): mobile by SINR, Wi-Fi by RSSI.
export function classifyMobile(sinr) {
  if (sinr === null || sinr === undefined || sinr === "" || isNaN(Number(sinr))) return null;
  const v = Number(sinr);
  if (v >= 13) return "strong";
  if (v >= 5) return "medium";
  if (v >= 0) return "weak";
  return "none";
}
export function classifyWifi(rssi) {
  if (rssi === null || rssi === undefined || rssi === "" || isNaN(Number(rssi))) return null;
  const v = Number(rssi);
  if (v >= -60) return "strong";
  if (v >= -70) return "medium";
  if (v >= -80) return "weak";
  return "none";
}

// Coverage of a zone: manual override wins if set; otherwise computed from the latest measurement.
export function computeZoneCoverage(zone) {
  if (!zone) return { level: "unverified", mode: "manual" };
  if (zone.coverageMode === "manual" && zone.coverage && zone.coverage !== "unverified") {
    return { level: zone.coverage, mode: "manual" };
  }
  const lastMobile = (zone.mobile || [])[0];
  const lastWifi = (zone.wifi || [])[0];
  let level = lastMobile ? classifyMobile(lastMobile.sinr) : null;
  if (!level && lastWifi) level = classifyWifi(lastWifi.rssi);
  if (level) return { level, mode: "computed" };
  return { level: "unverified", mode: "manual" };
}

export function zoneStats(zones) {
  const active = (zones || []).filter((z) => !z.archived);
  let verified = 0, pending = 0, none = 0;
  active.forEach((z) => {
    if (z.status === "verified") verified++; else pending++;
    if (computeZoneCoverage(z).level === "none") none++;
  });
  return { total: active.length, verified, pending, none };
}

export function defaultSignalZone(name = "") {
  return {
    id: newId("zone"),
    name, type: "", areaM2: "", location: "", lat: "", lng: "", description: "",
    building: "", operator: "", connectionType: "", wifiTech: "",
    status: "pending", coverage: "unverified", coverageMode: "manual",
    observations: "", problems: "", recommendations: "",
    wifi: [], mobile: [], photos: [], archived: false,
    plan: null,
  };
}

export const POINT_KINDS = ["wifi", "mobile"];
export function newPoint(patch = {}) {
  return {
    id: newId("pt"), zoneId: "", pageId: "", xPct: 50, yPct: 50, kind: "wifi",
    operator: "", technology: "", freq: "", rssi: "", download: "", upload: "", latency: "", sinr: "",
    coverage: "unverified", mode: "", note: "", photo: null, timestamp: new Date().toISOString(), ...patch,
  };
}
export function defaultBlueprint() {
  return { pages: [] };
}

export function newWifiEntry() {
  return { id: newId("wifi"), band: "", channel: "", rssi: "", download: "", upload: "", latency: "", point: "", mode: "", note: "", timestamp: new Date().toISOString() };
}
export function newMobileEntry() {
  return { id: newId("mob"), operator: "", technology: "", band: "", rsrp: "", rsrq: "", rssi: "", sinr: "", pci: "", point: "", mode: "", note: "", timestamp: new Date().toISOString() };
}

export function seedSignalZones() {
  const z1 = defaultSignalZone("Receção");
  Object.assign(z1, { type: "reception", areaM2: "120", status: "verified", operator: "CST", connectionType: "Fibra + 4G (backup)", wifiTech: "2,4 GHz / 5 GHz" });
  z1.wifi = [{ ...newWifiEntry(), band: "5 GHz", channel: "36", rssi: "-58", download: "68", upload: "22", latency: "14", point: "Balcão", mode: "measured" }];
  const z2 = defaultSignalZone("Restaurante");
  Object.assign(z2, { type: "restaurant", areaM2: "400", status: "verified", lat: "0.3365", lng: "6.7319", operator: "CST", connectionType: "Fibra + 4G (backup)", wifiTech: "2,4 GHz / 5 GHz", description: "Área de refeições interior e exterior.", observations: "Sinal estável na maior parte da área, com quedas de velocidade na zona exterior." });
  z2.wifi = [{ ...newWifiEntry(), band: "5 GHz", channel: "44", rssi: "-67", download: "45", upload: "12", latency: "28", point: "Mesa central", mode: "measured" }];
  const z3 = defaultSignalZone("Jardim / Exterior");
  Object.assign(z3, { type: "garden", areaM2: "900", status: "pending", coverage: "weak", coverageMode: "manual" });
  return [z1, z2, z3];
}

// --- HX Stable Energy (solar / BESS) ---
export const ENERGY_CHECKLIST = [
  { id: "panels_mounted", group: "solar" },
  { id: "panel_orientation", group: "solar" },
  { id: "string_voltage", group: "solar" },
  { id: "mppt_config", group: "solar" },
  { id: "dc_protection", group: "solar" },
  { id: "pv_grounding", group: "solar" },
  { id: "battery_mounting", group: "bess" },
  { id: "bms_status", group: "bess" },
  { id: "soc_calibration", group: "bess" },
  { id: "ventilation", group: "bess" },
  { id: "ac_protection", group: "bess" },
  { id: "inverter_config", group: "bess" },
  { id: "backup_switchover", group: "bess" },
  { id: "labeling_safety", group: "bess" },
];

// 5-state data provenance classification (spec section 4)
export const CLASS_MODES = ["measured", "documented", "estimated", "unverified", "simulated"];
// Checklist conformity states (spec section J)
export const CHECK_STATUSES = ["conforme", "nao_conforme", "nao_verificado", "nao_aplicavel"];
export const STUDY_OPTIONS = ["free", "paid"];
export const AUTH_STATUSES = ["pending", "sent", "signed"];
export const INSTALL_TYPES = ["hotel", "resort", "enterprise", "commerce", "industry", "other"];
export const ASSESS_STATES = ["draft", "in_progress", "completed", "review"];
export const SUPPLY_TYPES = ["grid", "generator", "hybrid", "other"];
export const PHASE_OPTIONS = ["mono", "tri"];
export const LOAD_PROFILE_SOURCES = ["meter_history", "ems", "manual", "none"];
export const PRIORITIES = ["high", "medium", "low"];
export const PHOTO_CATEGORIES = [
  "counter", "main_board", "transformer", "generators", "nameplates",
  "bess_location", "solar_area", "connection_points", "other",
];

// legacy -> new checklist status migration
const CK_STATUS_MIGRATE = { ok: "conforme", issue: "nao_conforme", na: "nao_aplicavel" };

export function defaultEnergy() {
  return {
    authStatus: "pending",
    client: { name: "", company: "", nif: "", installLocation: "", representative: "", role: "", contacts: "", date: "", studyOption: "free" },
    survey: {
      // A — installation identification
      projectCode: "", installName: "", installType: "", gps: "", engineer: "", techContact: "", visitDate: "", visitTime: "", assessmentStatus: "draft",
      // B — energy consumption
      monthlyKwh: "", invoiceHistory: "", avgDailyKwh: "", peakKw: "", contractedPower: "", peakHours: "", mainLoads: "", energyCost: "",
      // C — load profile
      loadProfileSource: "", loadProfileNotes: "",
      // D — electrical grid
      supplyType: "", nominalVoltage: "", frequency: "", phases: "", transformer: "", transformerCapacity: "", mainBoard: "", protections: "", visualState: "", connectionPoints: "", singleLine: "",
      // F — interruptions & autonomy
      outageFrequency: "", outageDuration: "", failureHistory: "", infoOrigin: "", affectedEquip: "", continuityNeeds: "", desiredAutonomyH: "", loadPriority: "",
      // H — solar area
      solarSurface: "", solarAreaM2: "", orientation: "", tilt: "", shading: "", structuralState: "", distanceToConn: "",
      // I — BESS area
      bessLocation: "", bessSpace: "", bessIndoorOutdoor: "", ventilation: "", temperature: "", accessibility: "", proximityBoard: "", safetyConditions: "",
      // Preliminary Solar/BESS analysis
      proposedSolarKwp: "", proposedBessKwh: "", inverterPower: "", desiredAutonomy: "", estimatedAutonomy: "", operationStrategy: "", techConstraints: "", missingData: "", nextPhaseRecs: "",
      notes: "",
    },
    generators: [],
    criticalLoads: [],
    loadReadings: [],
    loadFiles: [],
    fieldMeta: {},
    checklist: {},
    checkDetails: {},
    photos: [],
  };
}

// Normalize/migrate any stored energy object to the current schema without losing data.
export function migrateEnergy(e) {
  const d = defaultEnergy();
  if (!e) return d;
  const survey = { ...d.survey, ...(e.survey || {}) };
  // migrate legacy "assessment" fields into the new survey/analysis schema
  const a = e.assessment || {};
  if (a.siteName && !survey.installName) survey.installName = a.siteName;
  if (a.solarKwp && !survey.proposedSolarKwp) survey.proposedSolarKwp = a.solarKwp;
  if (a.batteryKwh && !survey.proposedBessKwh) survey.proposedBessKwh = a.batteryKwh;
  if (a.inverterModel && !survey.inverterPower) survey.inverterPower = a.inverterModel;
  if (a.systemVoltage && !survey.nominalVoltage) survey.nominalVoltage = a.systemVoltage;
  if (a.notes && !survey.notes) survey.notes = a.notes;
  // migrate legacy checklist statuses
  const checklist = {};
  Object.entries(e.checklist || {}).forEach(([k, v]) => { checklist[k] = CK_STATUS_MIGRATE[v] || v; });
  // migrate legacy checkDetails mode values (old 3-state is a subset of the new set)
  const checkDetails = e.checkDetails || {};
  return {
    ...d,
    ...e,
    authStatus: e.authStatus || d.authStatus,
    client: { ...d.client, ...(e.client || {}) },
    survey,
    generators: e.generators || [],
    criticalLoads: e.criticalLoads || [],
    loadReadings: e.loadReadings || [],
    loadFiles: e.loadFiles || [],
    fieldMeta: e.fieldMeta || {},
    checklist,
    checkDetails,
    photos: e.photos || [],
  };
}

// Downscale + compress an uploaded image to a small JPEG data URL (localStorage-safe).
export function compressImage(file, maxDim = 480, quality = 0.6) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width >= height && width > maxDim) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else if (height > maxDim) {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// --- Deterministic signal diagnostics (transparent rules, NO AI) ---
const LEVEL_ORDER = { good: 0, fair: 1, warning: 2, critical: 3 };

export function levelRsrp(v) {
  if (v == null || isNaN(v)) return null;
  if (v < -110) return "critical";
  if (v < -100) return "warning";
  if (v < -90) return "fair";
  return "good";
}
export function levelRsrq(v) {
  if (v == null || isNaN(v)) return null;
  if (v < -20) return "critical";
  if (v < -15) return "warning";
  if (v < -10) return "fair";
  return "good";
}
export function levelSinr(v) {
  if (v == null || isNaN(v)) return null;
  if (v < 0) return "critical";
  if (v < 5) return "warning";
  if (v < 13) return "fair";
  return "good";
}

export const SIGNAL_RANGES = {
  RSRP: "good ≥ -90 · fair -90..-100 · warning -100..-110 · critical < -110 dBm",
  RSRQ: "good ≥ -10 · fair -10..-15 · warning -15..-20 · critical < -20 dB",
  SINR: "good ≥ 13 · fair 5..13 · warning 0..5 · critical < 0 dB",
};

// returns { findings:[{metric,value,unit,level,ruleKey,range}], overall }
export function diagnoseSignal(m) {
  if (!m) return { findings: [], overall: null };
  const defs = [
    { metric: "RSRP", value: m.rsrp, unit: "dBm", level: levelRsrp(m.rsrp) },
    { metric: "RSRQ", value: m.rsrq, unit: "dB", level: levelRsrq(m.rsrq) },
    { metric: "SINR", value: m.sinr, unit: "dB", level: levelSinr(m.sinr) },
  ];
  const findings = defs
    .filter((d) => d.level !== null)
    .map((d) => ({
      ...d,
      ruleKey: `rule.${d.metric.toLowerCase()}.${d.level}`,
      range: SIGNAL_RANGES[d.metric],
    }));
  let overall = null;
  findings.forEach((f) => {
    if (overall === null || LEVEL_ORDER[f.level] > LEVEL_ORDER[overall]) overall = f.level;
  });
  return { findings, overall };
}

// returns { causes:[key], measures:[key], steps:[key] } — deterministic from findings
export function signalRecommendations(findings) {
  const lv = {};
  findings.forEach((f) => (lv[f.metric] = f.level));
  const bad = (m) => lv[m] === "warning" || lv[m] === "critical";
  const causes = new Set();
  const measures = new Set();
  const steps = new Set();

  if (bad("RSRP")) {
    causes.add("rc.cause.distance");
    causes.add("rc.cause.obstruction");
    measures.add("rc.meas.other_locations");
    steps.add("rc.step.reposition");
    steps.add("rc.step.external_antenna");
  }
  if (bad("SINR")) {
    causes.add("rc.cause.interference");
    causes.add("rc.cause.overlap");
    measures.add("rc.meas.pci_scan");
    steps.add("rc.step.reduce_interf");
  }
  if (bad("RSRQ")) {
    causes.add("rc.cause.congestion");
    causes.add("rc.cause.interference");
    measures.add("rc.meas.repeat_offpeak");
    measures.add("rc.meas.other_band");
    steps.add("rc.step.change_band");
  }
  if (causes.size === 0) {
    causes.add("rc.cause.none");
    steps.add("rc.step.none");
  }
  return { causes: [...causes], measures: [...measures], steps: [...steps] };
}

// Group REAL field measurements by location + technology + day (period) for CST vs Unitel compare.
export function buildFieldComparison(measurements) {
  const groups = {};
  (measurements || [])
    .filter((m) => m.origin === "field_manual")
    .forEach((m) => {
      const date = (m.timestamp || "").slice(0, 10);
      const loc = (m.location && m.location.trim()) || "—";
      const key = `${loc}||${m.technology}||${date}`;
      if (!groups[key]) groups[key] = { key, location: loc, technology: m.technology, date, cst: null, unitel: null };
      const slot = m.operator === "CST" ? "cst" : m.operator === "Unitel STP" ? "unitel" : null;
      if (slot) {
        const cur = groups[key][slot];
        if (!cur || new Date(m.timestamp) > new Date(cur.timestamp)) groups[key][slot] = m;
      }
    });
  return Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));
}

// higher is better for all three; returns 'cst' | 'unitel' | 'tie' | null
export function betterOperator(cst, unitel, metricKey) {
  if (!cst || !unitel) return null;
  const a = cst[metricKey];
  const b = unitel[metricKey];
  if (a == null || b == null) return null;
  if (a === b) return "tie";
  return a > b ? "cst" : "unitel";
}

// Live metrics for the Digital Twin — deterministic (reuses runScenario), adds throughput.
export function computeLiveMetrics(params = {}, deviceCounts = { ap: 4, client: 20 }) {
  const r = runScenario(params, deviceCounts);
  const apCount = deviceCounts.ap || 4;
  let tput;
  if (params.ispDown && !params.backupActive) {
    tput = 0;
  } else {
    tput = params.ispDown && params.backupActive ? 120 : 500; // backup link is slower
    if (params.apDown && apCount > 0) tput *= (apCount - 1) / apCount;
    if (params.powerLoss) tput *= 0.5;
    if (params.packetLoss) tput *= 0.92;
    if (params.latencyUp) tput *= 0.9;
    tput *= r.availability / 100;
  }
  return {
    availability: r.availability,
    latencyMs: r.latencyMs,
    packetLossPct: r.packetLossPct,
    throughputMbps: Math.round(tput),
  };
}

// --- Live Digital Twin: deterministic device/connection states from scenario params ---
export const TWIN_COLORS = { active: "#16A34A", warning: "#F59E0B", failure: "#DC2626", offline: "#94A3B8" };

// params: { powerLoss, ispDown, backupActive, apDown }
// returns { nodeStates:{id:'active'|'warning'|'failure'|'offline'}, edgeStates:{id:'active'|'warning'|'down'} }
export function computeTwinStates(topology, params = {}) {
  const nodes = topology?.nodes || [];
  const edges = topology?.edges || [];
  const sev = { active: 0, warning: 1, failure: 2, offline: 3 };
  const worse = (a, b) => (sev[a] >= sev[b] ? a : b);

  const ns = {};
  nodes.forEach((n) => (ns[n.id] = "active"));

  // 1) Power interruption — UPS sustains core (gateway/router/switch); other local gear goes offline.
  if (params.powerLoss) {
    nodes.forEach((n) => {
      const k = n.data?.kind;
      if (k === "isp") return; // external, independent of local power
      if (k === "ups") ns[n.id] = worse(ns[n.id], "warning"); // discharging / active backup
      else if (["gateway", "router", "switch"].includes(k)) ns[n.id] = worse(ns[n.id], "warning");
      else ns[n.id] = worse(ns[n.id], "offline");
    });
  }

  // 2) ISP + downstream internet effect.
  const downstreamNoInternet = params.ispDown && !params.backupActive; // powered but no WAN
  nodes.forEach((n) => {
    const k = n.data?.kind;
    if (k === "isp") {
      if (params.ispDown && !params.backupActive) ns[n.id] = "failure";
      else if (params.ispDown && params.backupActive) ns[n.id] = "warning"; // primary down, backup up
      // else stays active
    } else if (downstreamNoInternet) {
      ns[n.id] = worse(ns[n.id], "warning");
    }
  });

  // 3) Access point shutdown — first AP in order fails.
  if (params.apDown) {
    const ap = nodes.find((n) => n.data?.kind === "ap");
    if (ap) ns[ap.id] = worse(ns[ap.id], "failure");
  }

  // 4) Connections.
  const es = {};
  edges.forEach((e) => {
    const a = ns[e.source];
    const b = ns[e.target];
    if (a === "offline" || b === "offline" || a === "failure" || b === "failure") es[e.id] = "down";
    else if (a === "warning" || b === "warning") es[e.id] = "warning";
    else es[e.id] = "active";
  });

  return { nodeStates: ns, edgeStates: es };
}
